<template>
  <div class="container-nzidoo">
    <page-title title="Admin Permissions Manager" />

    <!-- Toolbar -->
    <div class="admin-toolbar">
      <div class="admin-search-box">
        <i class="mdi mdi-magnify admin-search-icon"></i>

        <input type="text" v-model="searchTerm" :placeholder="$t('sentence.searchByName')" class="admin-search-input">

        <!-- Clear icon -->
        <i v-if="searchTerm" class="mdi mdi-close-circle admin-clear-icon" @click="clearSearch"
          :title="$t('sentence.clear')"></i>
      </div>
    </div>

    <LoadingSpinner v-if="loader" />
    <!-- Admins Grid -->
    <div class="admin-grid">
      <div v-for="user in filteredUsers" :key="user.id" class="admin-card">
        <div class="admin-card-header">
          <div class="admin-card-actions">
            <!-- {{ user.firstname.charAt(0) }}{{ user.lastname.charAt(0) }} -->
          </div>
          <div class="admin-card-actions">
            <button class="btn btn-icon-action btn-warning" @click="getUserById(user.id)" title="View Permissions">
              <i class="mdi mdi-shield-account"></i>
            </button>
          </div>
        </div>
        <div class="admin-card-body">
          <div class="admin-card-section">
            <label class="admin-card-label">Administrator</label>
            <div class="card-name">{{ user.lastname }} {{ user.firstname }}</div>
          </div>
          <div class="admin-card-section">
            <label class="admin-card-label">Email</label>
            <div class="card-light">
              <i class="mdi mdi-email-outline"></i>
              {{ user.email }}
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="filteredUsers.length === 0" class="admin-empty-state">
        <svg class="admin-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="8"></circle>
          <path d="m21 21-4.35-4.35"></path>
        </svg>
        <p class="admin-empty-text">No administrators found</p>
      </div>
    </div>

    <!-- Drawer Backdrop -->
    <transition name="drawer-backdrop">
      <div v-if="selectedUser" class="admin-drawer-backdrop" @click="handleClose"></div>
    </transition>

    <!-- Drawer -->
    <transition name="drawer">
      <div v-if="selectedUser" class="admin-drawer-container">
        <div class="admin-drawer-header">
          <div>
            <h3 class="admin-drawer-title">Manage Permissions</h3>
            <p class="admin-drawer-subtitle">
              {{ selectedUser.firstname }} {{ selectedUser.lastname }}
            </p>
          </div>
          <button class="btn btn-close" @click="handleClose">
            <i class="mdi mdi-close"></i>
          </button>
        </div>

        <div class="admin-drawer-body">
          <!-- Add Permission Input -->
          <div class="admin-form-group">
            <label class="admin-form-label">{{ $t('action.add') }}</label>
            <div class="permissions-autocomplete-wrapper">
              <input type="text" class="admin-form-input" placeholder="Search for pages..." v-model="page"
                @focus="showAutocomplete = true" />

              <!-- Autocomplete Dropdown -->
              <div v-if="showAutocomplete && page && filteredAvailablePages.length > 0"
                class="permissions-autocomplete-dropdown">
                <div v-for="availablePage in filteredAvailablePages" :key="availablePage.id"
                  @click="handleAddPermission(availablePage)" class="permissions-autocomplete-item">
                  <i class="mdi mdi-plus-circle-outline"></i>
                  <span>{{ availablePage.text }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Current Permissions -->
          <div class="admin-form-group">
            <label class="admin-form-label">
              Current Permissions ({{ pages.length }})
            </label>

            <div class="permissions-container">
              <p v-if="pages.length === 0" class="permissions-no-permissions">
                <i class="mdi mdi-alert-circle-outline"></i>
                No permissions assigned
              </p>
              <div v-for="permission in pages" :key="permission.id" class="permissions-tag">
                <i class="mdi mdi-file-document-outline"></i>
                <span>{{ permission.text }}</span>
                <button @click="handleRemovePermission(permission.id)" class="permissions-tag-remove">
                  <i class="mdi mdi-close"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="admin-drawer-footer">
          <button @click="handleClose" class="btn btn-secondary">
            {{ $t('action.cancel') }}
          </button>
          <button @click="updateUser(selectedUser.id)" :disabled="loader" class="btn btn-primary">
            <i class="mdi mdi-check"></i>
            {{ $t('action.save') }}
          </button>
        </div>
      </div>
    </transition>



    <notifications style="top:5%;" width="35%" group="error" position="top center" />
    <notifications style="top:5%;" width="35%" group="success" position="top center" />
  </div>
</template>

<script>
import PageTitle from '@/components/templates/PageTitle.vue';
import LoadingSpinner from "@/components/templates/LoadingSpinner.vue";

export default {
  components: { PageTitle,LoadingSpinner },
  name: "users-list-row",

  data() {
    return {
      roles: "ROLE_ADMIN",
      users: [],
      permissions: [],
      pages: [],
      pagesAutoComplete: [],
      page: "",
      searchTerm: "",
      selectedUser: null,
      loader: false,
      showAutocomplete: false,
    };
  },

  computed: {
    filteredUsers() {
      if (!this.searchTerm) return this.users;
      const term=this.searchTerm.toLowerCase();
      return this.users.filter(user => {
        const fullName=`${user.firstname} ${user.lastname} ${user.email}`.toLowerCase();
        return fullName.includes(term);
      });
    },

    filteredAvailablePages() {
      if (!this.page) return [];
      const term=this.page.toLowerCase();
      return this.pagesAutoComplete.filter(p => {
        const hasPermission=this.pages.find(perm => perm.id===p.id);
        return !hasPermission&&p.text.toLowerCase().includes(term);
      });
    }
  },

  mounted() {
    this.getUsers();
    this.getPermissions();
    document.addEventListener('click', this.handleClickOutside);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },

  methods: {
    clearSearch() {
      this.searchTerm="";
    },
    getUsers() {
      var roles="ROLE_ADMIN";
      var self=this;
        this.loader=true;
      this.$store
        .dispatch("getUsers", { roles })
        .then((response) => {
          self.users=response.data;
        this.loader=false;
        })
        .catch(function () { });
    },

    getPermissions() {
      var self=this;
      this.$axios({
        url: "admin/permissions/listPages",
        method: "GET",
      })
        .then((response) => {
          self.pagesName=Object.values(response.data);
          for (var i=0; i<self.pagesName.length; i++) {
            self.pagesAutoComplete.push({
              id: self.pagesName[i].id,
              text: self.pagesName[i].page,
            });
          }
        })
        .catch(() => { });
    },

    getUserById(userId) {
      var self=this;
      var roles="ROLE_ADMIN";
      self.pages=[];
      this.$axios({
        url: "admin/list",
        method: "POST",
        data: { userId: userId, roles: roles },
      })
        .then((response) => {
          self.selectedUser=response.data;

          for (var key in self.selectedUser.permissions) {
            self.pages.push({
              id: self.selectedUser.permissions[key].id,
              text: self.selectedUser.permissions[key].page,
            });
          }

        })
        .catch(() => {
        });
    },

    updateUser(userId) {
      var self=this;
      var pages=this.pages;
      var data={ pages };
      self.loader=true;
      this.$axios({
        url: "admin/permissions/update/user/"+userId,
        method: "POST",
        data: data,
      })
        .then((res) => {
          this.$notify({
            group: "success",
            title: res.data.message,
            type: "success",
          });
          self.loader=false;
          self.handleClose();
          self.getUsers();
        })
        .catch((err) => {
          self.loader=false;
          this.$notify({
            group: "error",
            title: err||"Error updating user",
            type: "error",
          });
        });
    },

    handleAddPermission(pageItem) {
      const exists=this.pages.find(p => p.id===pageItem.id);
      if (!exists) {
        this.pages.push(pageItem);
      }
      this.page='';
      this.showAutocomplete=false;
    },

    handleRemovePermission(pageId) {
      this.pages=this.pages.filter(p => p.id!==pageId);
    },

    handleClose() {
      this.selectedUser=null;
      this.pages=[];
      this.page='';
      this.showAutocomplete=false;
    },

    handleClickOutside(event) {
      const autocomplete=document.querySelector('.permissions-autocomplete-dropdown');
      const input=document.querySelector('.permissions-autocomplete-wrapper input');
      if (autocomplete&&!autocomplete.contains(event.target)&&event.target!==input) {
        this.showAutocomplete=false;
      }
    }
  },
};
</script>

<style scoped src="@/assets/css/shared-admin-styles.css"></style>
<style scoped>


/* Autocomplete */
.permissions-autocomplete-wrapper {
  position: relative;
}

.permissions-autocomplete-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 2px solid var(--gray-200);
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 250px;
  overflow-y: auto;
  z-index: 100;
}

.permissions-autocomplete-item {
  padding: 12px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: background 0.2s;
  font-size: 14px;
  color: var(--gray-700);
  border-bottom: 1px solid var(--gray-100);
}

.permissions-autocomplete-item:last-child {
  border-bottom: none;
}

.permissions-autocomplete-item:hover {
  background: var(--gray-50);
}

.permissions-autocomplete-item i {
  color: var(--color-nz);
  font-size: 18px;
}

/* Permissions Container */
.permissions-container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  min-height: 120px;
  padding: 16px;
  background: var(--gray-50);
  border-radius: 10px;
  border: 2px dashed var(--gray-200);
}

.permissions-no-permissions {
  width: 100%;
  color: var(--gray-400);
  font-size: 14px;
  margin: auto;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.permissions-no-permissions i {
  font-size: 20px;
}

.permissions-tag {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%);
  color: var(--color-nz-300);
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  border: 1px solid rgba(38, 149, 255, 0.2);
  transition: all 0.2s ease;
}

.permissions-tag:hover {
  box-shadow: 0 2px 8px rgba(38, 149, 255, 0.2);
}

.permissions-tag i {
  font-size: 16px;
}

.permissions-tag-remove {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  color: var(--color-nz-300);
  transition: all 0.2s;
  border-radius: 4px;
  margin-left: 4px;
}

.permissions-tag-remove:hover {
  background: rgba(220, 38, 38, 0.1);
  color: #dc2626;
}

.permissions-tag-remove i {
  font-size: 14px;
}
</style>